import React from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import _, {round, isNil, isEmpty} from 'lodash';
import { CellRenderers } from './tableUtils';
import { LoadingIcon } from 'components/Icons';
import classNames from 'classnames';
import { getFeatureFlags_UNSAFE, useFeatureFlag } from 'contexts/FeatureFlagContext';
import { useGlobalSettingsContext } from 'contexts/GlobalSettingsContext';
import { useHistory } from 'react-router-dom';
import Vocabulary from 'components/Vocabulary';
import { unusedRecommendedToolsContent } from 'components/SubproductRiskScoreTag';
import { RISK_SCORE_RATINGS } from 'constants/RiskScoreConstants';
import { SEVERITY_NAMES } from 'views/Findings/constants/severity';
import ACRiskScoreTag from 'ac-components/ac-riskscore-tag/ACRiskScoreTag';
import CustomRiskDocLink from 'components/CustomRiskScoreDocLink';

const { Link: HyperLink } = Typography;

export const RiskScoreTag = ({ riskScore, v2Design }) => {
    const featureFlags = useFeatureFlag();
    const { inverseScoreEnabled, riskScoreEnabled, riskScoreRange } = useGlobalSettingsContext();
    const className = getRiskScoreSeverity(riskScore, inverseScoreEnabled, featureFlags.RISK_SCORE_3_0, riskScoreRange);
    const isRiskScoreV3 = featureFlags.RISK_SCORE_3_0 || v2Design;
    const text = !riskScoreEnabled ? "-" : isNil(riskScore)
        ? CellRenderers.VALUE_OR_NA()
        : `${round(+riskScore, 2)}${isRiskScoreV3 ? "" : "/100"}`;
    return (
        <Tag className={classNames(`risk-score-tag`, className, { v2: v2Design })}>
            {text}
        </Tag>
    );
};

export default RiskScoreTag;

export const RiskScoreTagV2 = ({riskScore, riskScoreInverted}) => {
    if (riskScore === undefined){
        return <LoadingIcon />
    }
    const className = getSeverityByRiskScore(riskScore, riskScoreInverted);
    return (
        <Tag className={`risk-score-tag v2 ${className}`} style={{minWidth: 30, borderRadius: 5, padding: 5}}>{(isNil(riskScore)) ? CellRenderers.VALUE_OR_NA() : round(+riskScore, 2)}</Tag>
    )
}

export function getSeverityByRiskScore(riskScore, riskScoreInverted) {
    if (isNil(riskScore)) {
        return "info";
    }
    riskScore = +riskScore;
    const lowSeverity = 'low';
    if (riskScore <= 5) {
        return riskScoreInverted ? 'active' : "critical";
    } else if (riskScore <= 20 && riskScore > 5) {
        return riskScoreInverted ? lowSeverity : "high";
    } else if (riskScore <= 80 && riskScore > 20) {
        return "medium";
    } else if (riskScore <= 95 && riskScore > 80) {
        return riskScoreInverted ? "high" : lowSeverity;
    } else {
        return riskScoreInverted  ? "critical" : 'active';
    }
}

//this is used to get the severities for the risk score in app for risk score v3 algorithm changes
export function getSeverityByRiskScoreV3(riskScore, riskScoreRange) {
    if (isNil(riskScore)) {
        return "info";
    }
    riskScore = +riskScore;
    if(riskScoreRange) {
        if (riskScore > riskScoreRange[2]) {
            return 'critical';
        } else if (riskScore >  riskScoreRange[1] && riskScore <= riskScoreRange[2]) {
            return "high";
        } else if (riskScore >  riskScoreRange[0] && riskScore <=  riskScoreRange[1]) {
            return "medium";
        } else{
            return "active";
        }
    }
    else return "info";
}

//this checks RISK_SCORE_3_0 flag, if flag is off it returns old severity else new severity
export function getRiskScoreSeverity(riskScore, riskScoreInverted, riskScoreV3flag, riskScoreRange){
    return riskScoreV3flag ? getSeverityByRiskScoreV3(riskScore, riskScoreRange) : getSeverityByRiskScore(riskScore, riskScoreInverted, true)
}

// This is also used for AATI score color in feeds detail modal.
export function getSeverityByBaseScore(baseScore) {
    if (baseScore >= 0.1 && baseScore <= 3.9) {
        return 'low';
    } else if (baseScore >= 4 && baseScore <= 6.9) {
        return 'medium';
    } else if (baseScore >= 7 && baseScore <= 8.9) {
        return 'high';
    } else if (baseScore >= 9 && baseScore <= 10) {
        return 'critical';
    } else {
        return 'none';
    }
}

export function riskScoreMappedValue(riskScore) {
    if (riskScore <= 5) {
        return convertRange(riskScore, 0, 5, 0, 20)
    } else if (riskScore <= 20 && riskScore > 5) {
        return convertRange(riskScore, 6, 20, 21, 40)
    } else if (riskScore <= 80 && riskScore > 20) {
        return convertRange(riskScore, 21, 80, 41, 60)
    } else if (riskScore <= 95 && riskScore > 80) {
        return convertRange(riskScore, 81, 95, 61, 80)
    } else {
        return convertRange(riskScore, 96, 100, 81, 100)
    }
}

export function convertRange(num, old_min, old_max, new_min, new_max) {
    return Math.round(((num - old_min) / (old_max - old_min)) * (new_max - new_min) + new_min);
}

export const getRiskScorePopoverTitleProps = (riskScore, riskScoreInverted, riskScoreRange, riskScoreV3Flag) => {
    const riskSeverity = getRiskScoreSeverity(riskScore, riskScoreInverted, riskScoreV3Flag, riskScoreRange);
    switch (riskSeverity) {
        case "critical":
            return { title: <><Vocabulary name="riskScore"/> is <Vocabulary name="veryPoorRiskScore"/></>, titleStyle: riskSeverity }
        case "high":
            return { title: <><Vocabulary name="riskScore"/> is <Vocabulary name="poorRiskScore"/></>, titleStyle: riskSeverity }
        case "medium":
            return { title: <><Vocabulary name="riskScore"/> is <Vocabulary name="fairRiskScore"/></>, titleStyle: riskSeverity }
        case "active":
        case "low":
            return { title: <><Vocabulary name="riskScore"/> is <Vocabulary name="goodRiskScore"/></>, titleStyle: riskSeverity }             
        default:
            return {};
    }
}

export function getTrend(current, prev){
    if(current === prev){
        return 0;
    }
    if(current > prev){
        return 1;
    }
    return -1;
}

export const RiskScoreDisableTag = ({ v2Design ,history }) => { 
    const historyNav = useHistory();
    const featureFlags = useFeatureFlag();
    const onClick = (e) => {
        e.stopPropagation();
        const path = featureFlags.RISK_SCORE_3_0 ? '/globalsettings?activeStep=assetScore' : '/globalsettings?activeStep=step4';
        history ? history.push(path) : historyNav.push(path);
      };

    return (
        <Tooltip title={<><Vocabulary name="riskScore"/> is disabled. Please visit the global settings by clicking the risk score card</>}>
        <span onClick={onClick}><ACRiskScoreTag value={undefined}/></span> 
      </Tooltip>
    )
}

export const getRiskScoreEscalationDisplayFormula = (finding, escalationConfig) => {
    if (isEmpty(finding)) {
        return null;
    }
    const { riskScore, findingScore, assetScore } = finding;

    if (escalationConfig) {
        const { escalationTags, maxOrganicScore, escalatedScore } = escalationConfig;
        const safeEscalationTags = Array.isArray(escalationTags) ? escalationTags : [];
        const findingTags = finding.tags || [];
        const isEscalated = safeEscalationTags.some(tag =>
            findingTags.some(ft => ft.key === tag || ft.value === tag || ft === tag)
        );
        if (isEscalated) {
            const organicRiskScore = finding.additionalDetails?.organicRiskScore;
            return (
                <div>
                    <div><strong>Escalated Risk Score: {escalatedScore}</strong></div>
                    <div><strong>Organic Risk Score</strong></div>
                    <div>
                        <strong>{organicRiskScore ?? "N/A"}</strong> =
                        MIN({maxOrganicScore}, (<Vocabulary name="findingScore" />(
                        <strong>{findingScore}</strong>) x <Vocabulary name="assetScore" />(
                        <strong>{assetScore}</strong>)))
                    </div>
                </div>
            );
        } else if (maxOrganicScore != null) {
            return (
                <div>
                    <strong>{riskScore}</strong> = MIN(<strong>{maxOrganicScore}</strong>, <Vocabulary name="findingScore" />(
                    <strong>{findingScore}</strong>) x <Vocabulary name="assetScore" />(
                    <strong>{assetScore}</strong>))
                </div>
            )
        }
    }

    return getRiskScoreDisplayFormula(finding);
}

export const getRiskScoreDisplayFormula = (finding) => {
    if(isEmpty(finding)){
        return null;
    }

    const { riskScore, findingScore, assetScore } = finding;

    return  getFeatureFlags_UNSAFE().CUSTOM_RISK_ENABLED ? (
        <div style = {{maxWidth: 400}}>
          This risk score is calculated via a custom risk scoring model. Please refer to your organization's <CustomRiskDocLink /> for details.
        </div>
      ) : (
        <div>
          <strong>{riskScore}</strong> = <Vocabulary name="findingScore" />(
          <strong>{findingScore}</strong>) × <Vocabulary name="assetScore" />(
          <strong>{assetScore}</strong>)
        </div>
      );
}


export const OverallRiskScoreTooltipContents = ({riskScore, filtersData = {product: [], subProduct: []}}) =>{
    const {inverseScoreEnabled: riskScoreInverted, riskScoreRange, maxRiskScore } = useGlobalSettingsContext();
    const featureFlags = useFeatureFlag();
    const riskySubproduct = riskScore?.mostRiskyProduct?.mostRiskySubProduct;
    const riskyFindings = riskScore?.mostRiskyProduct?.mostRiskySubProduct.riskyFinding;
    const showNewScoreReason = riskScore?.mostRiskyProduct?.mostRiskySubProduct.unusedRecommendedTools;
    const unusedToolCount = showNewScoreReason?.length ?? 0;
    const mostRiskyProduct = riskScore?.mostRiskyProduct || {};
    const { score, riskCalculationType } = riskScore || {};
    const isRiskScoreExcellent = score === 0;
    const popoverTitleProps = getRiskScorePopoverTitleProps(score, riskScoreInverted, riskScoreRange, featureFlags.RISK_SCORE_3_0);
    const tooltipTitle = <Typography.Text strong className={`${popoverTitleProps?.titleStyle}-text`}>
                            {popoverTitleProps?.title}
                         </Typography.Text>
    const UnusedToolsTooltipContent = <>{unusedToolCount && unusedRecommendedToolsContent(showNewScoreReason, unusedToolCount, maxRiskScore)}</>
    const RiskyFindingsTooltipContent = <div className="flex-1 m-r-l p-r-l">Riskiest finding id is {" "}
    <HyperLink target="_blank" href={`#/findings/${riskyFindings?.id}`} onClick={(e) => e.stopPropagation()} >
        {riskyFindings?.id}
    </HyperLink></div>
    const SingleSubproductMeanTooltipContent = !_.isEmpty(showNewScoreReason) ? UnusedToolsTooltipContent : RiskyFindingsTooltipContent;
    if (isRiskScoreExcellent) {
        return tooltipTitle;
    } 
    if(riskCalculationType === "RISKY" && riskySubproduct && riskyFindings && !unusedToolCount){
          return (
            <>
              {tooltipTitle}
              {getRiskScoreDisplayFormula(riskyFindings)}
              <div>
                      {
                         ((_.isEmpty(filtersData.subProduct)) && (_.isEmpty(filtersData.product) || _.size(filtersData.product) > 1) ) ?
                              <div className="flex-1 m-r-l p-r-l">
                                  Riskiest <Vocabulary name="product" isLowerCase /> is{" "}
                                  <HyperLink target="_blank" href={`#/product/${mostRiskyProduct.id}`} onClick={(e) => e.stopPropagation()} >
                                      {mostRiskyProduct.name}
                                  </HyperLink>
                              </div> :
                              (!_.isEmpty(filtersData.product) && _.size(filtersData.product) === 1 &&  (_.isEmpty(filtersData.subProduct) || _.size(filtersData.subProduct) > 1)) ?
                               <div className="flex-1 m-r-l p-r-l">
                                   Riskiest <Vocabulary name="subProduct" isLowerCase /> is{" "}
                                   <HyperLink target="_blank" href={`#/subProduct/${riskySubproduct.id}`} onClick={(e) => e.stopPropagation()} >
                                       {riskySubproduct.name}
                                   </HyperLink>
                               </div> :
                                  (!_.isEmpty(filtersData.subProduct) && _.size(filtersData.subProduct) === 1) ?
                                  RiskyFindingsTooltipContent :
                                <></>
                          
                } 
            </div>
            <RiskScoreDelayeUpdate/>
        </>);
    }

    if(riskCalculationType === "AVERAGE"){
        return (
            <>
                {tooltipTitle}
                {
                         (_.isEmpty(filtersData.product) || _.size(filtersData.product) > 1) ?
                          <li>The mean <Vocabulary name="riskScore"/> across all <Vocabulary name="subProduct" isPlural={true} isLowerCase/> is calculated to be {score}</li> :
                        (!_.isEmpty(filtersData.product) && _.size(filtersData.product) === 1 &&  (_.isEmpty(filtersData.subProduct) || _.size(filtersData.subProduct) > 1)) ?
                          <li>The average <Vocabulary name="riskScore"/> across all <Vocabulary name="subProduct" isPlural={true} isLowerCase/> is {score}</li> :
                        (!_.isEmpty(filtersData.product) && _.size(filtersData.product) === 1 &&  (!_.isEmpty(filtersData.subProduct) && _.size(filtersData.subProduct) === 1)) ?
                           SingleSubproductMeanTooltipContent : <></>
                }              
            </>
          );
    }
    
    if(!_.isEmpty(showNewScoreReason)) {
        return <>
        {UnusedToolsTooltipContent} 
        <RiskScoreDelayeUpdate/>
        </>
    }
    return null;
};

export const riskScoreRiskyTooltipContentV1 = (
    filtersData,
    mostRiskyProduct
  ) => {
    return !_.isEmpty(filtersData.product) || !_.isEmpty(filtersData.subProduct) ? (
      <>
        {mostRiskyProduct?.mostRiskySubProduct?.name} is the most risky{" "}
        <Vocabulary name="subProduct" />
      </>
    ) : (
      <>
        {mostRiskyProduct?.name} is the most risky <Vocabulary name="product" />
      </>
    );
};

export const RISK_SCORE_DOC_LINK = (
  <Typography.Link
    style={{ color: "white" }}
    target="_blank"
    href="/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/21909345208595-Risk-Score"
    onClick={(e) => e.stopPropagation()}
  >
    Learn More
  </Typography.Link>
);

export const RiskScoreDelayeUpdate = () => {
    return (
        <div className={"m-t-xs"}>
            <div className="p-r-xs">If <Vocabulary name="riskScore" /> or <Vocabulary name="findingScore" /> are modified,<br/> expect a delay in the updates. </div>
            <div className="text-right tag-tooltip-doc-link">{RISK_SCORE_DOC_LINK}</div>
        </div>
    );
};

export function displayRiskRatingCount({riskRatingsSource = {}, showFair = true, showGood = true, onClick, dataOnClick, renderTooltip}) {
    const excludeRiskRatingKeys = [];
    if(!showFair){
        excludeRiskRatingKeys.push(RISK_SCORE_RATINGS.FAIR);
    }
    if(!showGood){
        excludeRiskRatingKeys.push(RISK_SCORE_RATINGS.GOOD);
    }
    const riskRatingDisplayKeys = Object.keys(_.omit(RISK_SCORE_RATINGS, excludeRiskRatingKeys)) || [];
    const severityColorKeys = Object.keys(SEVERITY_NAMES);
    return (<span className={classNames({"severity-counts-container": renderTooltip})}>
        {
            _.map(riskRatingDisplayKeys, (riskRatingKey, i) => {
                if(riskRatingsSource[riskRatingKey]){
                    return <RiskRatingCountRenderer className={classNames({[severityColorKeys[i]]: renderTooltip}, i === riskRatingDisplayKeys.length - 1 ? "m-r-0" : "")} riskRating={riskRatingKey} count={riskRatingsSource[riskRatingKey]} onClick={onClick} dataOnClick={dataOnClick} renderTooltip={renderTooltip}/>
                }
                return null;
            })
        }
    </span>)
}

export function displayRiskRatingCountWithTooltip({riskRatingsSource = {}, onClick, dataOnClick}) {
    const riskRatingDisplayKeys = Object.keys(RISK_SCORE_RATINGS) || [];
    return (<span className={classNames("riskrating-counts-container")}>
        {
            _.map(riskRatingDisplayKeys, (riskRatingKey, i) => {
                return <RiskRatingCountWithTooltipRenderer className={classNames(_.lowerCase([riskRatingDisplayKeys[i].replace("_","")]), i === riskRatingDisplayKeys.length - 1 ? "m-r-0" : "")} riskRating={riskRatingKey} count={riskRatingsSource[riskRatingKey]} onClick={onClick} dataOnClick={dataOnClick}/>
            })
        }
    </span>)
}

function RiskRatingCountRenderer({riskRating = "", className = "", count, onClick, dataOnClick}) {
    const isClickable = onClick && typeof onClick === "function";

    return _.isNil(count) ? null : <Tag className={`${_.lowerCase(`${riskRating.replace("_", "")}`)} ${className} ${isClickable ? "clickable" : ""} severity-tag`} onClick={() => onClick(_.upperCase(riskRating), dataOnClick, true)}><span className="finding-text" title={_.capitalize(riskRating)}>{(count) || 0}</span>&nbsp;{ _.upperCase(riskRating[0])}&nbsp;</Tag>
}

function RiskRatingCountWithTooltipRenderer({riskRating = "", className = "", count, onClick, dataOnClick}) {
    const isClickable = onClick && typeof onClick === "function";
    return (
        <Tooltip title={`${_.capitalize(riskRating.replace("_", " "))}: ${count ? count : 0}`}>
            <span className={classNames("riskrating-counts-box", className, { clickable: isClickable })} onClick={() => onClick(_.upperCase(riskRating), dataOnClick, true)}>
                <span className='riskrating-count-value'>
                    {count}
                </span>
                <span className='riskrating-count-indicator'>
                    {_.upperCase(riskRating[0])}
                </span>
            </span>
        </Tooltip>
    ) 
}

export const RiskRatings = {
    goodRiskScore: "goodRiskScore",
    fairRiskScore: "fairRiskScore",
    poorRiskScore: "poorRiskScore",
    veryPoorRiskScore: "veryPoorRiskScore",
};

export const RiskRatingKeys = Object.keys(RiskRatings);

export function getRiskRatingFromRiskScore(
  riskScore,
  riskScoreRange,
) {

  if (riskScore <= riskScoreRange[0]) return "goodRiskScore";
  if (riskScore > riskScoreRange[0] && riskScore <= riskScoreRange[1])
    return "fairRiskScore";
  if (riskScore > riskScoreRange[1] && riskScore <= riskScoreRange[2])
    return "poorRiskScore";
  if (riskScore > riskScoreRange[2]) return "veryPoorRiskScore";
}